import { isPWA } from './isPwa';

/**
 * Subtle haptic feedback for the installed app (PWA/TWA) only.
 * Patterns follow Android HapticFeedbackConstants / Apple HIG: short, sparse, meaningful.
 * iOS Safari ignores navigator.vibrate, so this is a no-op there.
 */
const patterns = {
  /** Toggle, chip, filter, tab change (CLOCK_TICK / selection). */
  selection: 12,
  /** Long-press, pull-to-refresh threshold (LONG_PRESS / GESTURE_THRESHOLD). */
  impact: 20,
  /** Booking confirmed and other completed tasks (CONFIRM). */
  success: [16, 70, 28],
  /** Failed request / validation error (REJECT). */
  error: [32, 60, 32, 60, 32],
} as const;

export type HapticType = keyof typeof patterns;

const isApp = () => {
  if (typeof window === 'undefined') return false;
  if (isPWA()) return true;
  // The app shell opens the first page with ?application=true; remember it for later navigations.
  if (new URLSearchParams(window.location.search).get('application') === 'true') {
    sessionStorage.setItem('p24-app-shell', '1');
    return true;
  }
  return sessionStorage.getItem('p24-app-shell') === '1';
};

export const haptic = (type: HapticType) => {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
    if (!isApp()) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    navigator.vibrate(patterns[type] as VibratePattern);
  } catch {
    // Vibration is best-effort; never break the action.
  }
};

export default haptic;
